// Each city's network bundle, loaded when that city is in force and not before.
//
// `src/data/network.<city>.json` is every station and line of one city, and six
// of them together were 170 KB of JSON in the entry chunk of an app that shows
// one. Each city that was added made /app's first load slower for everyone
// else, which is how Perth took the Lighthouse gate under its floor. They are
// dynamic imports now, one chunk per city, so a phone fetches the city it uses.
//
// The readers (`src/network.js`, `src/game/lines.js`) stay synchronous, which is
// what the line strip and every pure function over a bundle rely on. They read
// from this cache, and whoever changes the city in force awaits `loadNetwork`
// FIRST: `main.js` before mount, `switchCity` before `setCity`, the Signal Box
// before it draws. A city that is not loaded reads as empty, never as another
// city's data.
//
// A SPECIFIER PER CITY, written out, not `import(\`./data/network.${id}.json\`)`:
// the bundler can only split a computed import by guessing a glob, and a new
// city should be a line here that the guard test (tests/entry-chunk.test.js)
// can see.

import { ref } from 'vue';
import { onBackground, onForeground } from './foreground.js';

const LOADERS = {
  melbourne: () => import('./data/network.json'),
  sydney: () => import('./data/network.sydney.json'),
  brisbane: () => import('./data/network.brisbane.json'),
  adelaide: () => import('./data/network.adelaide.json'),
  canberra: () => import('./data/network.canberra.json'),
  perth: () => import('./data/network.perth.json'),
};

const loaded = {};

/**
 * Bumped whenever a bundle arrives. The readers in `src/network.js` read it, so
 * anything computed from a station or a line re-runs once its city is in,
 * instead of keeping the empty answer it was given while the chunk was on the
 * way.
 */
export const networkVersion = ref(0);

/** True while the boot load has failed twice and is being retried in the background. */
export const networkFailed = ref(false);
const pending = {};

/** The cities this file can load a bundle for. */
export const NETWORK_CITIES = Object.keys(LOADERS);

/** Fetch (once) and keep one city's bundle. Resolves to the bundle, or null for an unknown city. */
export function loadNetwork(id) {
  const key = String(id);
  if (loaded[key]) return Promise.resolve(loaded[key]);
  if (!LOADERS[key]) return Promise.resolve(null);
  pending[key] ??= LOADERS[key]()
    .then((m) => { loaded[key] = m.default ?? m; networkVersion.value += 1; return loaded[key]; })
    .finally(() => { delete pending[key]; });
  return pending[key];
}

/** A bundle already loaded, or null. Never starts a fetch. */
export function loadedNetwork(id) {
  return loaded[String(id)] ?? null;
}

const RETRY_MS = 20_000;
const RETRY_MAX_MS = 5 * 60_000;
let retrying = null;

/**
 * The boot load of the city in force: one retry straight away, then, if that
 * fails too, `networkFailed` goes up (Home says so) and it is tried again when
 * the phone comes back online, when the app returns to the foreground, and on
 * a timer that backs off (20 s, 40 s, 80 s ... never more than five minutes
 * apart) until it lands. The timer PAUSES while the page is hidden or the app is
 * in the background: a phone in a pocket asking every twenty seconds for a file
 * it will not show achieves nothing but battery and requests. Coming back to
 * the front tries at once and starts the backoff again. Never rejects; resolves
 * true or false.
 *
 * Everything at boot that reads a station awaits THIS, not `loadNetwork`.
 */
export async function bootNetwork(id) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      await loadNetwork(id);
      networkFailed.value = false;
      return true;
    } catch { /* retry once, then say so */ }
  }
  networkFailed.value = true;
  retryInBackground(id);
  return false;
}

/** The delay before retry number `failures` (0 is the first): doubling, capped. */
export function retryDelay(failures) {
  return Math.min(RETRY_MS * 2 ** Math.max(0, failures), RETRY_MAX_MS);
}

function retryInBackground(id) {
  if (retrying) return;
  let failures = 0;
  let timer = null;
  // `hidden` covers the web and most WebViews; the foreground bus covers the
  // native `appStateChange`, which fires when a WebView never says hidden.
  let away = typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const stopTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
  const schedule = () => {
    stopTimer();
    if (away) return; // paused: waking up restarts it
    timer = setTimeout(attempt, retryDelay(failures));
  };
  function attempt() {
    timer = null;
    loadNetwork(id).then(() => {
      networkFailed.value = false;
      retrying?.stop();
    }, () => {
      failures += 1;
      schedule();
    });
  }
  const wake = () => {
    if (away) return;
    // -1, because the attempt below is not one of the backoff steps: if it
    // fails, the next try is the first step (20 s), not the second.
    failures = -1;
    stopTimer();
    attempt();
  };
  const onVisibility = () => {
    away = document.visibilityState === 'hidden';
    if (away) stopTimer(); else wake();
  };
  const onOnline = () => wake();
  const unBackground = onBackground(() => { away = true; stopTimer(); });
  const unForeground = onForeground(() => { away = false; wake(); });

  globalThis.addEventListener?.('online', onOnline);
  globalThis.document?.addEventListener?.('visibilitychange', onVisibility);
  retrying = {
    stop() {
      stopTimer();
      unBackground();
      unForeground();
      globalThis.removeEventListener?.('online', onOnline);
      globalThis.document?.removeEventListener?.('visibilitychange', onVisibility);
      retrying = null;
    },
  };
  schedule();
}
